import { HotTokensRowInfo } from 'api/types/httpsTypes/contracts';
import { FC, useEffect, useMemo, useState } from 'react';
import ErrorBoundary from 'components/error-boundaries';
import Table from 'modules/shared-components/data-display/table';
import { useHistory } from 'react-router-dom';
import { thDynamicClass } from './index';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { useHotTokensSearchFilters } from './usePageParams';
import {
  TokenStatsWebSocketShortMessage,
  useTokenStats,
} from 'api/hooks/useTokenStats';
import React from 'react';
import { HotTokensPeriods } from '../Wallets/usePageParams';
import { useWebsocket } from 'api/hooks/useWebsocket';
import { transFormPeriodInMinutes } from 'api/services/httpServices/ContractService';

interface Props {
  columnsDef: HotTokensRowColumn<HotTokensRowInfo>[];
  data: HotTokensRowInfo;
}

export interface HotTokensRowColumn<T> {
  label?: string | JSX.Element;
  key: string;
  sortableKey?: string;
  className?: string;
  showOnMobile?: boolean;
  component?: (props: {
    data: T;
    tokenStats: TokenStatsWebSocketShortMessage | undefined | null;
    period: HotTokensPeriods;
  }) => JSX.Element;
}

export const TableRowComponent: FC<Props> = ({ data, columnsDef }) => {
  const history = useHistory();
  const isDesktop = useMediaQuery(screenGte.small);
  const { filtersState } = useHotTokensSearchFilters();
  const [tokenStats, setTokenStats] =
    useState<TokenStatsWebSocketShortMessage | null>(null);

  const statsSub = useMemo(
    () => ({
      chain: data.chain,
      address: data.tokenAddress,
      timeFrame: transFormPeriodInMinutes(filtersState.period),
    }),
    [data.chain, data.tokenAddress, filtersState.period]
  );

  useWebsocket('token_stats', statsSub);

  const rowTokenStats = useTokenStats(
    data.chain,
    data.tokenAddress,
    filtersState.period
  );

  useEffect(() => {
    setTokenStats(null);
  }, [filtersState]);

  useEffect(() => {
    if (rowTokenStats) {
      setTokenStats(rowTokenStats);
    }
  }, [rowTokenStats]);

  return (
    <tr
      onClick={() =>
        history.push({
          pathname: !isDesktop
            ? `/dex/research/${data.chain}/${data.tokenAddress}`
            : `/dex/snipe/new/${data.chain}/${data.tokenAddress}`,
          state: {
            from: `${history.location.pathname}${history.location.search}`,
          },
        })
      }
      className="border-t border-dex-black-700 lg:dark:bg-dex-black-800 cursor-pointer items-center hover:dark:lg:bg-dex-black-700 lg:grid lg:grid-cols-9"
    >
      <ErrorBoundary>
        {columnsDef.map((column) => {
          const Column = column.component;
          if (!Column) return;
          return (
            <Table.Td className={thDynamicClass(column.key)} key={column.key}>
              <Column
                data={data}
                tokenStats={tokenStats}
                period={filtersState.period}
              />
            </Table.Td>
          );
        })}
      </ErrorBoundary>
    </tr>
  );
};
export const TableRow = React.memo(TableRowComponent, (prev, next) => {
  return (
    prev.data.tokenAddress === next.data.tokenAddress &&
    prev.data.chain === next.data.chain &&
    prev.columnsDef === next.columnsDef
  );
});
